import React, { useEffect, useState, useCallback } from 'react';
import { ArrowRight, X } from 'lucide-react';

// OnboardingTour — dead-simple spotlight overlay. Dims the whole page,
// cuts a hole around the target element, and floats an arrow + message
// beside it explaining the next step. Runs once per user (persisted in
// localStorage) unless the caller passes `force`.
//
// Props:
//   steps: [{ target: '[data-tour="foo"]', title, body, cta? }]
//   storageKey: string  — persistence key
//   force: bool         — ignore the "seen it" flag (for a manual replay)
//   onFinish: () => void
//
// Design note: no external tour libraries (Shepherd/Joyride) — keeps
// bundle small and CSS-in-JS keeps styling isolated from the app theme.

function findTarget(selector) {
  if (!selector) return null;
  try { return document.querySelector(selector); } catch { return null; }
}

function OnboardingTour({ steps = [], storageKey, force = false, onFinish }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const step = steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = findTarget(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (!steps.length) return undefined;
    if (!force) {
      try {
        if (localStorage.getItem(storageKey)) return undefined;
      } catch { /* ignore */ }
    }
    setVisible(true);
    // Give the page one paint to settle before measuring.
    const t = setTimeout(measure, 200);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure, storageKey, force, steps.length]);

  useEffect(() => { measure(); }, [i, measure]);

  function finish() {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    onFinish?.();
  }

  function next() {
    if (i + 1 >= steps.length) { finish(); return; }
    setI(i + 1);
  }

  if (!visible || !step) return null;

  const PAD = 8; // spotlight halo
  const spotX = rect ? rect.x - PAD : 0;
  const spotY = rect ? rect.y - PAD : 0;
  const spotW = rect ? rect.w + PAD * 2 : 0;
  const spotH = rect ? rect.h + PAD * 2 : 0;

  // Position the tooltip below the target when there's room, else above.
  const CARD_W = 320;
  const CARD_H_EST = 180;
  const belowY = spotY + spotH + 12;
  const aboveY = spotY - CARD_H_EST - 12;
  const cardY = (rect && belowY + CARD_H_EST < window.innerHeight) ? belowY : Math.max(20, aboveY);
  const cardX = rect
    ? Math.min(window.innerWidth - CARD_W - 16, Math.max(16, spotX + spotW / 2 - CARD_W / 2))
    : Math.max(16, window.innerWidth / 2 - CARD_W / 2);

  return (
    <>
      {/* Dim + cut-out. Uses a big box-shadow trick so we don't need
          SVG masks: the spotlight is a small transparent rect and the
          shadow spreads darkness in all directions. */}
      <div
        aria-hidden="true"
        onClick={next}
        style={{
          position: 'fixed',
          zIndex: 5000,
          top: 0, left: 0, right: 0, bottom: 0,
          pointerEvents: rect ? 'none' : 'auto',
          background: rect ? 'transparent' : 'rgba(15,23,42,0.55)',
        }}
      />
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            zIndex: 5001,
            top: spotY,
            left: spotX,
            width: spotW,
            height: spotH,
            borderRadius: 12,
            boxShadow: `
              0 0 0 9999px rgba(15,23,42,0.55),
              0 0 0 3px #0ea5a4,
              0 0 24px 4px rgba(14,165,164,0.7)
            `,
            pointerEvents: 'none',
            transition: 'all 220ms ease',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        role="dialog"
        aria-label={step.title}
        style={{
          position: 'fixed',
          zIndex: 5002,
          left: cardX,
          top: cardY,
          width: CARD_W,
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          boxShadow: '0 20px 40px rgba(15,23,42,0.24)',
          padding: '16px 18px 14px',
          color: '#0f172a',
        }}
      >
        <button
          type="button"
          onClick={finish}
          aria-label="Skip tour"
          style={{
            position: 'absolute',
            top: 6, right: 8,
            background: 'transparent', border: 0, cursor: 'pointer',
            color: '#94a3b8', padding: 4,
          }}
        >
          <X size={14} />
        </button>

        <div style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#0ea5a4',
          marginBottom: 4,
        }}>
          Step {i + 1} of {steps.length}
        </div>
        <h4 style={{ margin: '0 0 6px 0', fontSize: 15 }}>{step.title}</h4>
        <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
          {step.body}
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={finish}
            style={{
              background: 'transparent', border: 0, cursor: 'pointer',
              color: '#94a3b8', fontSize: 12,
            }}
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={next}
            className="btn btn-primary btn-sm"
          >
            {step.cta || (i + 1 === steps.length ? 'Got it' : 'Next')} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </>
  );
}

export default OnboardingTour;
